/**
 * Keyboard (04 Keyboard summary):
 * `Space` play · `F` fullscreen · `R` reset view · `Esc` unfollow · `←/→` ±10 s ·
 * `Shift+←/→` ±lap · `,` `.` events · `[` `]` rate · `D` director · `+`/`-` zoom · `H` toggle chrome ·
 * `M` sound · `C` captions ·
 * `?` help overlay · `` ` `` debug overlay.
 */
export interface KeyboardActions {
  togglePlay(): void;
  toggleFullscreen(): void;
  resetView(): void;
  unfollow(): void;
  jumpSeconds(dt: number): void;
  jumpLap(dir: -1 | 1): void;
  jumpEvent(dir: -1 | 1): void;
  stepRate(dir: -1 | 1): void;
  director(): void;
  zoom(factor: number): void;
  toggleChrome(): void;
  toggleHelp(): void;
  closeHelp(): boolean;
  toggleDebug(): void;
  toggleSound(): void;
  toggleCaptions(): void;
  seekStart(): void;
  seekEnd(): void;
  /** style key dispatch (03 §10): called for keys the shell does not handle itself; true = the
   *  active style handled it (the shell then preventDefaults) */
  styleKey(key: string): boolean;
}

/** The help overlay's table (key → action), shared with help.ts. */
export const KEY_HELP: readonly (readonly [string, string])[] = [
  ['Space', 'Play / pause'],
  ['D', 'Director mode (the Director time warp)'],
  ['M', 'Sound on / off (on after START)'],
  ['C', 'Captions on / off (team radio, commentary)'],
  ['[  ]', 'Free rate down / up (1× 5× 20× 60×)'],
  ['← →', '−10 s / +10 s'],
  ['Shift + ← →', 'Previous / next lap (leader lap start)'],
  [',  .', 'Previous / next story event'],
  ['Home  End', 'Race start / end'],
  ['+  −', 'Zoom in / out (wheel zooms at the cursor)'],
  ['Drag', 'Pan the map'],
  ['Click a car / tower row', 'Follow that car'],
  ['Esc', 'Release follow / close this help'],
  ['R', 'Reset the view to the style preset'],
  ['F', 'Fullscreen'],
  ['H', 'Hide / show the chrome'],
  ['`', 'Debug overlay'],
  ['?', 'This help'],
];

function isEditable(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // buttons inside the ? overlay (race switch) keep Space/Enter for themselves
  return tag === 'BUTTON' && t.closest('#help, .help') !== null;
}

export function bindKeyboard(target: Document, a: KeyboardActions): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (isEditable(e.target)) return;
    const k = e.key;
    let handled = true;
    switch (k) {
      case ' ':
      case 'Spacebar':
        a.togglePlay();
        break;
      case 'f':
      case 'F':
        a.toggleFullscreen();
        break;
      case 'r':
      case 'R':
        a.resetView();
        break;
      case 'Escape':
        if (!a.closeHelp()) a.unfollow();
        break;
      case 'ArrowLeft':
        if (e.shiftKey) a.jumpLap(-1);
        else a.jumpSeconds(-10);
        break;
      case 'ArrowRight':
        if (e.shiftKey) a.jumpLap(1);
        else a.jumpSeconds(10);
        break;
      case ',':
        a.jumpEvent(-1);
        break;
      case '.':
        a.jumpEvent(1);
        break;
      case '[':
        a.stepRate(-1);
        break;
      case ']':
        a.stepRate(1);
        break;
      case 'd':
      case 'D':
        a.director();
        break;
      case '+':
      case '=':
        a.zoom(1.15);
        break;
      case '-':
      case '_':
        a.zoom(1 / 1.15);
        break;
      case 'h':
      case 'H':
        a.toggleChrome();
        break;
      case 'm':
      case 'M':
        a.toggleSound();
        break;
      case 'c':
      case 'C':
        a.toggleCaptions();
        break;
      case '?':
        a.toggleHelp();
        break;
      case '`':
        a.toggleDebug();
        break;
      case 'Home':
        a.seekStart();
        break;
      case 'End':
        a.seekEnd();
        break;
      default:
        // style-specific keys (StyleModule.onKey): only what the shell leaves unhandled, so a
        // style can never shadow a shell key
        handled = a.styleKey(k);
    }
    if (handled) e.preventDefault();
  };
  target.addEventListener('keydown', onKey);
  return () => {
    target.removeEventListener('keydown', onKey);
  };
}
